import { includes } from "lodash";
import { AbstractDate } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
} from "@radarly/models/dataviz";
import { SearchFragmentTerm } from "@radarly/models/search";
import { getDefaultTermMetricsCardDataviz } from "../dataviz";
import { isTermMetricsCard } from "../is";
import { getMetricsFromCard, getPrimaryMetricFromCard } from "../metric";
import { Card, TermMetricsCard } from "../model";
import { TermMetricsCardTerm } from "../term";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";

type Metrics =
  | [Metric.Reach | Metric.Engagement, Metric.Reach | Metric.Engagement]
  | [Metric.Reach, Metric.Engagement, Metric.Doc | Metric.Repost];
interface TermMetricsCardFactoryOptions {
  from?: AbstractDate;
  metrics?: Metrics;
  to?: AbstractDate;
  clause?: Clause;
  term?: TermMetricsCardTerm;
}

function getValidMetrics(metrics: Metric[]): Metrics {
  if (!metrics) {
    return [Metric.Reach, Metric.Engagement, Metric.Doc];
  }
  const validMetrics = [
    includes([Metric.Reach, Metric.Engagement], metrics[0])
      ? metrics[0]
      : Metric.Reach,
    includes([Metric.Reach, Metric.Engagement], metrics[1])
      ? metrics[1]
      : Metric.Engagement,
    includes([Metric.Doc, Metric.Repost], metrics[2]) ? metrics[2] : Metric.Doc,
  ];
  return validMetrics as Metrics;
}

function getValidMetricsFromCardAndInput(
  card: Card,
  input?: TermMetricsCardFactoryOptions
) {
  if (input && input.metrics) return input.metrics;
  if (isTermMetricsCard(card)) return getMetricsFromCard(card) as Metrics;
  return [
    Metric.Reach,
    Metric.Engagement,
    getPrimaryMetricFromCard(card) || Metric.Doc,
  ];
}

export class TermMetricsCardFragmentsFactory extends CardFragmentsFactory {
  static create(input?: TermMetricsCardFactoryOptions) {
    const factory = new TermMetricsCardFragmentsFactory();
    return factory.getFragments(input);
  }
  public getFragments(
    input?: TermMetricsCardFactoryOptions
  ): [SearchFragmentTerm<Metrics>] {
    return [this.getFragment(input)];
  }
  private getFragment(
    input?: TermMetricsCardFactoryOptions
  ): SearchFragmentTerm<Metrics> {
    const term = input?.term || Facet.Platforms;
    const validMetrics = getValidMetrics(input?.metrics);
    return {
      request: {
        query: this.getQuery(input),
        // @ts-ignore
        aggs: {
          metrics: { metrics: validMetrics },
          [term]: {
            metrics: validMetrics,
          },
        },
      },
    };
  }
}
export class TermMetricsCardFactory
  implements ICardFactory<TermMetricsCard, TermMetricsCardFactoryOptions>
{
  public static getInstance() {
    return new TermMetricsCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: TermMetricsCardFactoryOptions & {
      dataviz?: TermMetricsCard["dataviz"];
    }
  ) {
    const dataviz = getDefaultTermMetricsCardDataviz(
      input?.dataviz || card.dataviz
    );
    const factory = new TermMetricsCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    return CardFactory.getInstance<TermMetricsCard>().createCard(
      workspaceId,
      projectFocuses,
      "term-metrics",
      card.title,
      factory.getFragments({
        clause: factory.getClauseFromCard(card),
        from,
        to,
        term: factory.getTermFromCard(card, input) as TermMetricsCardTerm,
        metrics: getValidMetrics(getValidMetricsFromCardAndInput(card, input)),
      }),
      dataviz
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: TermMetricsCardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<TermMetricsCard>().createCard(
      workspaceId,
      projectFocuses,
      "term-metrics",
      title,
      TermMetricsCardFragmentsFactory.create(input),
      getDefaultTermMetricsCardDataviz(
        input?.fullDataviz ??
          getDefaultDataviz(input?.dataviz || DatavizName.BubbleChart)
      )
    );
  }
}
